import { useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, Outlet, useParams, useLocation } from 'react-router'
import { Provider as JotaiProvider, useAtomValue } from 'jotai'
import { JSONUIProvider, Renderer } from '@json-render/react'
import { ShikiThemeProvider, TooltipProvider } from '@neo-agents/ui'
import { Toaster } from './components/ui/sonner'
import { AuthProvider } from './contexts/AuthContext'
import { WorkspaceProvider } from './contexts/WorkspaceContext'
import { WebSocketProvider } from './contexts/WebSocketContext'
import { WorkspaceGate } from './components/WorkspaceGate'
import { useAuth } from './contexts/AuthContext'
import { useWorkspace } from './contexts/WorkspaceContext'
import { useRoleGuard } from './hooks/useRoleGuard'
import { useChatRouteSync } from './hooks/useChatRouter'
import { useChatbotActions } from './state/actions'
import { useSessionsInit } from './hooks/useSessionsInit'
import { useChat } from './hooks/useChat'
import { isDarkAtom } from './state/store'
import { loadTheme } from './lib/storage'
import { chatManifest, adminManifest } from './catalog/manifest'
import { componentRegistry } from './catalog/registry'
import { AdminLoginPage } from './pages/AdminLoginPage'
import { LoggedOutPage } from './pages/LoggedOutPage'
import { StudentLoginPage } from './pages/StudentLoginPage'
import { UnauthorizedPage } from './pages/UnauthorizedPage'
import { AuthLoadingState } from './components/AuthLoadingState'

// Admin page imports (default exports from page components)
import UsagePage from './app/(admin)/admin/usage/page'
import WorkspacesPage from './app/(admin)/admin/workspaces/page'
import MyClassesPage from './app/(admin)/admin/my-classes/page'
import ClassSettingsDetailPage from './app/(admin)/admin/class-settings/[classId]/page'
import StudentsPage from './app/(admin)/admin/students/page'
import TeachersPage from './app/(admin)/admin/teachers/page'
import FlaggedPage from './app/(admin)/admin/flagged/page'

// ─── Route guards ─────────────────────────────────────────────────────────────

function ProtectedRoute() {
  const { isLoading, isAuthenticated } = useAuth()

  if (isLoading || !isAuthenticated) {
    return <AuthLoadingState />
  }

  return <Outlet />
}

function hasAuthRole(roles: string[] | undefined, expectedRole: string): boolean {
  return roles?.some((role) => role.toUpperCase() === expectedRole) ?? false
}

function PublicAdminRoute() {
  const { isLoading, isAuthenticated, user } = useAuth()
  const { isResolved, memberRole, error } = useWorkspace()
  const isAdminFromAuth = hasAuthRole(user?.roles, 'ADMIN')

  if (isLoading || (isAuthenticated && !isResolved && !error)) {
    return <AuthLoadingState label="Loading session..." />
  }

  if (isAuthenticated) {
    if (isAdminFromAuth) return <Navigate to="/admin/workspaces" replace />
    if (memberRole === 'STUDENT') return <Navigate to="/" replace />
    if (memberRole === 'ADMIN') return <Navigate to="/admin/workspaces" replace />
    return <Navigate to="/admin/usage" replace />
  }

  return <Outlet />
}

function PublicStudentRoute() {
  const { isLoading, isAuthenticated, user } = useAuth()
  const { isResolved, memberRole, error } = useWorkspace()
  const isAdminFromAuth = hasAuthRole(user?.roles, 'ADMIN')
  const isTeacherFromAuth = hasAuthRole(user?.roles, 'TEACHER')

  if (isLoading || (isAuthenticated && !isResolved && !error)) {
    return <AuthLoadingState label="Loading session..." />
  }

  if (isAuthenticated) {
    if (isAdminFromAuth) return <Navigate to="/admin/workspaces" replace />
    if (isTeacherFromAuth) return <Navigate to="/admin/usage" replace />
    if (memberRole === 'ADMIN' || memberRole === 'TEACHER') {
      return <Navigate to="/admin/usage" replace />
    }
    return <Navigate to="/" replace />
  }

  return <Outlet />
}

function AdminGuard() {
  const { user } = useAuth()
  const { pathname } = useLocation()
  const { isAllowed, isLoading } = useRoleGuard(['ADMIN', 'TEACHER'])
  const isWorkspaceManagement = pathname === '/admin/workspaces'
  const isAdminFromAuth = hasAuthRole(user?.roles, 'ADMIN')

  if (isWorkspaceManagement && isAdminFromAuth) {
    return <Outlet />
  }

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-background">
        <span className="text-sm text-muted-foreground">Loading...</span>
      </div>
    )
  }

  if (!isAllowed) return null

  return <Outlet />
}

function AdminIndexRedirect() {
  return <Navigate to="/admin/workspaces" replace />
}

function AdminLegacyClassRedirect() {
  const { classId } = useParams<{ classId: string }>()
  return <Navigate to={classId ? `/admin/my-classes/${classId}` : '/admin/my-classes'} replace />
}

// ─── Chat shell ───────────────────────────────────────────────────────────────

function ChatShell() {
  const { chatId, classId } = useParams<{ chatId?: string; classId?: string }>()
  useChatRouteSync(chatId ?? null, classId ?? null)
  return <Renderer tree={chatManifest} registry={componentRegistry} />
}

function ChatShellWithInit() {
  useSessionsInit()
  useChat()
  return <ChatShell />
}

// ─── Admin shell ──────────────────────────────────────────────────────────────

function AdminShell() {
  return <Renderer tree={adminManifest} registry={componentRegistry} />
}

// ─── App routes (inside all providers) ───────────────────────────────────────

function AppRoutes() {
  const { handlers } = useChatbotActions()

  return (
    <JSONUIProvider
      registry={componentRegistry}
      initialData={{}}
      actionHandlers={handlers}
    >
      <Routes>
        {/* Public auth routes */}
        <Route element={<PublicAdminRoute />}>
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/admin/logged-out" element={<LoggedOutPage audience="admin" />} />
        </Route>
        <Route element={<PublicStudentRoute />}>
          <Route path="/login" element={<StudentLoginPage />} />
          <Route path="/logged-out" element={<LoggedOutPage audience="student" />} />
        </Route>
        <Route path="/auth/signin" element={<Navigate to="/admin/login" replace />} />
        <Route path="/auth/callback/robolabx" element={<Navigate to="/admin/login" replace />} />

        {/* Protected routes */}
        <Route element={<ProtectedRoute />}>
          <Route element={<WorkspaceGate />}>
            {/* Chat */}
            <Route path="/" element={<ChatShellWithInit />} />
            <Route path="/classes/:classId" element={<ChatShellWithInit />} />
            <Route path="/classes/:classId/chats/:chatId" element={<ChatShellWithInit />} />
            <Route path="/c/:chatId" element={<ChatShellWithInit />} />

            {/* Admin unauthorized — no role guard */}
              <Route path="/admin/unauthorized" element={<UnauthorizedPage />} />

            {/* Admin — role guarded */}
            <Route element={<AdminGuard />}>
              <Route path="/admin" element={<AdminShell />}>
                <Route index element={<AdminIndexRedirect />} />
                <Route path="workspaces" element={<WorkspacesPage />} />
                <Route path="my-classes" element={<MyClassesPage />} />
                <Route path="my-classes/:classId" element={<ClassSettingsDetailPage />} />
                <Route path="classes/:classId" element={<AdminLegacyClassRedirect />} />
                <Route path="usage" element={<UsagePage />} />
                <Route path="class-settings" element={<Navigate to="/admin/my-classes" replace />} />
                <Route path="class-settings/:classId" element={<AdminLegacyClassRedirect />} />
                <Route path="groups" element={<Navigate to="/admin/my-classes" replace />} />
                <Route path="students" element={<StudentsPage />} />
                <Route path="teachers" element={<TeachersPage />} />
                <Route path="flagged" element={<FlaggedPage />} />
              </Route>
            </Route>

            {/* Catch-all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Route>
      </Routes>
    </JSONUIProvider>
  )
}

// ─── Theme layer ──────────────────────────────────────────────────────────────

function ThemedApp() {
  const isDark = useAtomValue(isDarkAtom)
  const shikiTheme = isDark ? 'dracula' : 'github-light'

  return (
    <ShikiThemeProvider shikiTheme={shikiTheme}>
      <TooltipProvider>
        <AppRoutes />
        <Toaster position="bottom-right" />
      </TooltipProvider>
    </ShikiThemeProvider>
  )
}

// ─── Root export ──────────────────────────────────────────────────────────────

export function App() {
  useEffect(() => {
    const saved = loadTheme()
    const isDark = saved
      ? saved === 'dark'
      : window.matchMedia('(prefers-color-scheme: dark)').matches
    document.documentElement.classList.toggle('dark', isDark)
  }, [])

  return (
    <BrowserRouter>
      <JotaiProvider>
        <AuthProvider>
          <WorkspaceProvider>
            <WebSocketProvider>
              <ThemedApp />
            </WebSocketProvider>
          </WorkspaceProvider>
        </AuthProvider>
      </JotaiProvider>
    </BrowserRouter>
  )
}
